/* ------------------------------
   PRICING ADMIN — ROW AUTOSAVE (pure helpers + Supabase calls)

   Each Pricing Admin row saves on its own when the user leaves a field.
   Only the fields that changed are sent, so a save can never overwrite a
   price someone else changed on another row (or another field of this row)
   since the page loaded. The old page upserted all ~580 rows on every save.
   ------------------------------ */

export const PRICING_ITEM_COLUMNS =
  "id, name, cost, unit, active, sort_order, category, category_id, proposal_description, deleted_at";

export type PricingAdminItem = {
  id: string;
  name: string;
  cost: number | null;
  unit: string | null;
  active: boolean;
  sort_order: number | null;
  category: string | null;
  category_id: number | string | null;
  proposal_description: string | null;
  deleted_at: string | null;
};

/** Fields the page lets a user change on an existing row. */
export const EDITABLE_FIELDS = [
  "name",
  "unit",
  "cost",
  "sort_order",
  "proposal_description",
  "active",
  "deleted_at",
  // Moving an item: the Estimator matches items by the category TEXT
  // (pricing.ts filters on item.category), so both always move together.
  "category",
  "category_id",
] as const;

export type EditableField = (typeof EDITABLE_FIELDS)[number];
export type ItemPatch = Partial<Pick<PricingAdminItem, EditableField>>;

export const FIELD_LABELS: Record<EditableField, string> = {
  name: "name",
  unit: "unit",
  cost: "cost",
  sort_order: "sort",
  proposal_description: "description",
  active: "active",
  deleted_at: "trash",
  category: "category",
  category_id: "category",
};

// Text fields: null and "" mean the same thing; name is saved trimmed.
const textEq = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? "") === (b ?? "");

function normalizeValue(field: EditableField, v: any) {
  if (field === "name") return (v ?? "").trim();
  if (field === "active") return !!v;
  return v ?? null;
}

/** The fields that differ between the last-saved row and the row on screen. */
export function rowPatch(
  saved: PricingAdminItem,
  current: PricingAdminItem
): ItemPatch {
  const patch: ItemPatch = {};
  for (const f of EDITABLE_FIELDS) {
    const a = normalizeValue(f, (saved as any)[f]);
    const b = normalizeValue(f, (current as any)[f]);
    const same =
      f === "unit" || f === "proposal_description" || f === "name" || f === "category"
        ? textEq(a, b)
        : f === "category_id"
        ? (a == null ? null : Number(a)) === (b == null ? null : Number(b))
        : a === b;
    if (!same) (patch as any)[f] = b;
  }
  return patch;
}

/** A plain-English reason the patch can't be saved, or null if it's fine. */
export function validatePatch(patch: ItemPatch): string | null {
  if ("name" in patch && !(patch.name ?? "").trim()) {
    return "Name can't be blank.";
  }
  if ("cost" in patch && (patch.cost == null || !Number.isFinite(patch.cost))) {
    return "Enter a price (0 is fine).";
  }
  if (
    "sort_order" in patch &&
    (patch.sort_order == null || !Number.isFinite(patch.sort_order))
  ) {
    return "Enter a sort number.";
  }
  return null;
}

/** A price as the page shows it: comma-grouped like the proposal (1,400),
 *  up to two decimals (7.75, 1.5), never a dollar sign. */
export function formatPrice(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return "";
  return n.toLocaleString("en-US", { maximumFractionDigits: 2 });
}

/** What the user typed in a price box → number. Accepts "1,400", "$1400",
 *  " 7.75 ". Blank → null; anything else unreadable → NaN (refused on save). */
export function parsePrice(text: string): number | null {
  const t = (text || "").replace(/[$,\s]/g, "");
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : NaN;
}

/** Short description of a patch for the Undo button, e.g. "cost of Trex Railing". */
export function describePatch(patch: ItemPatch, itemName: string): string {
  const name = (itemName || "item").trim() || "item";
  if ("deleted_at" in patch) {
    return patch.deleted_at ? `moved ${name} to Trash` : `restored ${name}`;
  }
  const fields = Array.from(
    new Set((Object.keys(patch) as EditableField[]).map((f) => FIELD_LABELS[f]))
  );
  return `${fields.join(", ")} of ${name}`;
}

function cleanRow(r: any): PricingAdminItem {
  return {
    ...r,
    active: !!r.active,
    deleted_at: r.deleted_at ?? null,
    category_id: r.category_id == null ? null : Number(r.category_id),
  };
}

type SaveResult = { row: PricingAdminItem } | { error: string };

/** Update only the changed columns of one row; returns the row as saved. */
export async function updateItemFields(
  client: any,
  id: string,
  patch: ItemPatch
): Promise<SaveResult> {
  try {
    const { data, error } = await client
      .from("pricing_items2")
      .update(patch)
      .eq("id", id)
      .select(PRICING_ITEM_COLUMNS)
      .maybeSingle();
    if (error) return { error: error.message || "Save failed." };
    if (!data) {
      return { error: "This item wasn't found. Click Refresh and try again." };
    }
    return { row: cleanRow(data) };
  } catch (e: any) {
    return { error: e?.message || "Save failed — check your connection." };
  }
}

/** Insert a new row. The database assigns the id (never a browser-made one). */
export async function insertItem(
  client: any,
  item: PricingAdminItem
): Promise<SaveResult> {
  const { id: _tempId, ...rest } = item;
  const values = { ...rest, name: (rest.name || "").trim() };
  try {
    const { data, error } = await client
      .from("pricing_items2")
      .insert(values)
      .select(PRICING_ITEM_COLUMNS)
      .single();
    if (error) return { error: error.message || "Save failed." };
    if (!data) return { error: "Save failed." };
    return { row: cleanRow(data) };
  } catch (e: any) {
    return { error: e?.message || "Save failed — check your connection." };
  }
}

/* ------------------------------
   CATEGORIES
   ------------------------------ */

export type PricingAdminCategory = {
  id: number;
  name: string;
  sort_order: number;
  is_active: boolean;
};

const CATEGORY_COLUMNS = "id, name, sort_order, is_active";

type CatResult = { cat: PricingAdminCategory } | { error: string };

const cleanCat = (c: any): PricingAdminCategory => ({
  ...c,
  id: Number(c.id),
  is_active: !!c.is_active,
});

/** Create a category. The database assigns the id. */
export async function insertCategory(client: any, name: string): Promise<CatResult> {
  try {
    const { data, error } = await client
      .from("pricing_categories")
      .insert({ name: name.trim(), is_active: true, sort_order: 0 })
      .select(CATEGORY_COLUMNS)
      .single();
    if (error) return { error: error.message || "Couldn't add the category." };
    if (!data) return { error: "Couldn't add the category." };
    return { cat: cleanCat(data) };
  } catch (e: any) {
    return { error: e?.message || "Couldn't add the category — check your connection." };
  }
}

/** Show or hide a category in the Estimator. */
export async function setCategoryActive(
  client: any,
  id: number,
  isActive: boolean
): Promise<CatResult> {
  try {
    const { data, error } = await client
      .from("pricing_categories")
      .update({ is_active: isActive })
      .eq("id", id)
      .select(CATEGORY_COLUMNS)
      .maybeSingle();
    if (error) return { error: error.message || "Save failed." };
    if (!data) return { error: "This category wasn't found. Click Refresh." };
    return { cat: cleanCat(data) };
  } catch (e: any) {
    return { error: e?.message || "Save failed — check your connection." };
  }
}

/** Rename a category. The Estimator matches items by their category TEXT,
 *  so every item in it (Trash included) is renamed first, then the category
 *  row. Both steps only set the new name, so if either fails, running it
 *  again (Retry) finishes the job without harm. */
export async function renameCategory(
  client: any,
  id: number,
  newName: string
): Promise<CatResult> {
  const name = newName.trim();
  try {
    const items = await client
      .from("pricing_items2")
      .update({ category: name })
      .eq("category_id", id)
      .select("id");
    if (items.error) {
      return { error: items.error.message || "Couldn't rename the items." };
    }
    const { data, error } = await client
      .from("pricing_categories")
      .update({ name })
      .eq("id", id)
      .select(CATEGORY_COLUMNS)
      .maybeSingle();
    if (error) return { error: error.message || "Couldn't rename the category." };
    if (!data) return { error: "This category wasn't found. Click Refresh." };
    return { cat: cleanCat(data) };
  } catch (e: any) {
    return { error: e?.message || "Rename failed — check your connection." };
  }
}

/** Why a new or renamed category name can't be used, or null if it's fine. */
export function checkCategoryName(
  name: string,
  existing: { id: number; name: string }[],
  usedByPricing: (name: string) => string | null,
  selfId?: number
): string | null {
  const n = name.trim();
  if (!n) return "Give the category a name.";
  const key = (s: string) => s.toLowerCase().trim().replace(/[\s_-]+/g, "_");
  if (existing.some((c) => c.id !== selfId && key(c.name) === key(n))) {
    return "There's already a category with that name.";
  }
  const reason = usedByPricing(n);
  if (reason) return `${reason} Pick another name.`;
  return null;
}
